import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { ThroughputAdapter, ThroughputResponse } from '../../types';

/** WHERE MORE HARDWARE WOULD HELP. Per adapter: how many fetches, how many worked, how long
 *  each took, how full it ran, and how long requests waited for it. An adapter that runs
 *  near full while requests wait long for it is the one to give more room. */
export default function ThroughputPanel() {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState<ThroughputResponse | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setData(null);
    api
      .getFetchThroughput(hours)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read throughput'));
  }, [hours]);

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs">
        {[24, 72, 168].map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => setHours(h)}
            className={`px-2 py-1 rounded border ${
              hours === h
                ? 'border-blue-500 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            {h === 24 ? 'Last day' : h === 72 ? '3 days' : 'Week'}
          </button>
        ))}
      </div>
      {!data ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-3 font-normal">Adapter</th>
                <th className="py-1 pr-3 font-normal">Fetches</th>
                <th className="py-1 pr-3 font-normal">Worked</th>
                <th className="py-1 pr-3 font-normal">Time each</th>
                <th className="py-1 pr-3 font-normal" title="Time spent fetching, over the room it had, in its busiest hour">
                  Busiest hour
                </th>
                <th className="py-1 pr-3 font-normal" title="How long requests waited in the queue before it took them">
                  Wait
                </th>
                <th className="py-1 font-normal">Hour by hour</th>
              </tr>
            </thead>
            <tbody>
              {data.adapters.map((a) => (
                <Row key={a.adapter} a={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ a }: { a: ThroughputAdapter }) {
  const tried = a.hours.filter((h) => h.tries > 0);
  const secs = tried.reduce((s, h) => s + h.seconds, 0);
  const busiest = Math.max(0, ...a.hours.map((h) => h.busy_share ?? 0));
  const waits = a.hours.filter((h) => h.wait_s != null);
  const wait = waits.length ? waits.reduce((s, h) => s + (h.wait_s ?? 0), 0) / waits.length : null;
  return (
    <tr className="border-t border-gray-100 dark:border-gray-700">
      <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-100">
        {a.label}
        <div className="text-xs text-gray-400">room {a.room}</div>
      </td>
      <td className="py-1.5 pr-3">{a.tries.toLocaleString()}</td>
      <td className="py-1.5 pr-3">{a.tries ? `${Math.round((a.ok / a.tries) * 100)}%` : '-'}</td>
      <td className="py-1.5 pr-3">{a.tries ? `${(secs / a.tries).toFixed(1)}s` : '-'}</td>
      <td className={`py-1.5 pr-3 ${busiest >= 0.8 ? 'text-amber-700 dark:text-amber-400 font-medium' : ''}`}>
        {a.room ? `${Math.round(busiest * 100)}%` : '-'}
      </td>
      <td className="py-1.5 pr-3">{wait != null ? `${wait.toFixed(0)}s` : '-'}</td>
      <td className="py-1.5">
        <div className="flex items-end gap-px h-6" aria-label="busy share by hour">
          {a.hours.map((h) => (
            <div
              key={h.hour}
              title={`${new Date(h.hour).toLocaleString()}: ${h.tries} fetches, ${Math.round((h.busy_share ?? 0) * 100)}% full, wait ${h.wait_s ?? 0}s`}
              className={`w-1.5 ${(h.busy_share ?? 0) >= 0.8 ? 'bg-amber-500' : 'bg-blue-400'}`}
              style={{ height: `${Math.max(2, Math.min(100, (h.busy_share ?? 0) * 100))}%` }}
            />
          ))}
        </div>
      </td>
    </tr>
  );
}
